import { Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";

import { OtpInputs } from "@/components/auth/OtpInputs";
import { accountApi, type AccountStatement, type AccountTx, type StatementPeriod, type WithdrawalResult } from "@/lib/api";
import { getAuth } from "@/lib/auth-storage";
import { JaziaToast } from "@/lib/jazia-toast";
import "@/styles/jazia-docs.css";

const RECEIPT_FOOT = "Jazia Technologies Ltd \u2022 support@jazia.co.ke \u2022 This is a computer-generated receipt.";
const STATEMENT_FOOT = "Jazia Technologies Ltd \u2022 Regulated payment services \u2022 Report any discrepancy within 30 days at support@jazia.co.ke.";

function token() {
  return getAuth()?.token || "";
}

export function kes(value: number, digits = 2) {
  return `KES ${Math.abs(Number(value || 0)).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: 2 })}`;
}

function plain(value: number) {
  return Math.abs(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fullDate(value: string) {
  const d = new Date(value);
  return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}, ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

function shortDate(value: string) {
  return new Date(value).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function isoDay(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function errorOf(error: unknown, fallback: string) {
  const err = error as Error & { toastTitle?: string };
  return { title: err.toastTitle || fallback, message: err.message || "Try again." };
}

function Shell({ label, wide, onClose, children }: { label: string; wide?: boolean; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="jz-docm" onClick={onClose}>
      <div className={`box print${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-label={label} onClick={(event) => event.stopPropagation()}>
        <button type="button" className="x noprint" aria-label="Close" onClick={onClose}>&times;</button>
        {children}
      </div>
    </div>
  );
}

// ---------- Receipt ----------

export function ReceiptModal({ tx, onClose }: { tx: AccountTx; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const receipt = tx.receipt;
  if (!receipt) return null;
  const tone = receipt.tone;
  const mark = tone === "bad" ? "\u2715" : tone === "warn" ? "!" : "\u2713";

  const download = async () => {
    setBusy(true);
    try {
      saveBlob(await accountApi.receiptPdf(token(), tx.id), `Jazia-receipt-${tx.receiptNo}.pdf`);
      JaziaToast.success("Receipt downloaded", "Your receipt was saved as a PDF.");
    } catch (error) {
      const { title, message } = errorOf(error, "Couldn't download the receipt");
      JaziaToast.error(title, message);
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    const text = [`${receipt.heading}: ${kes(receipt.amount)}`, fullDate(receipt.at), ...receipt.rows.map(([label, value]) => `${label}: ${value}`)].join("\n");
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ title: `Jazia receipt ${tx.receiptNo}`, text });
        return;
      }
      await navigator.clipboard.writeText(text);
      JaziaToast.success("Copied", "The receipt details were copied. Paste them to share.");
    } catch (error) {
      if ((error as Error).name !== "AbortError") JaziaToast.error("Couldn't share", "Download the receipt and share the PDF instead.");
    }
  };

  return (
    <Shell label={`Receipt ${tx.receiptNo}`} onClose={onClose}>
      <div className="rc">
        <div className={`tick${tone === "ok" ? "" : ` ${tone}`}`} aria-hidden="true">{mark}</div>
        <div className={`head-${tone}`}>{receipt.heading}</div>
        <div className="amt mono">{kes(receipt.amount)}</div>
        <div className="when">{fullDate(receipt.at)}</div>
        {receipt.rows.map(([label, value]) => (
          <div className="kv" key={label}><span>{label}</span><span>{value}</span></div>
        ))}
        <p className="btns noprint">
          <button type="button" className="btn" disabled={busy} onClick={download}>{busy ? "Preparing..." : "Download / print"}</button>
          <button type="button" className="btn g" onClick={share}>Share</button>
          {tx.sale ? <Link className="btn o" to="/sales/$saleId" params={{ saleId: tx.sale.id }}>Open sale</Link> : null}
        </p>
        <div className="foot">{RECEIPT_FOOT}</div>
      </div>
    </Shell>
  );
}

// ---------- Statement ----------

function csvCell(value: string | number | null) {
  const text = value == null ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export function statementSearch(search: Record<string, unknown>): { from?: string; to?: string } {
  const out: { from?: string; to?: string } = {};
  if (typeof search["from"] === "string" && DAY.test(search["from"])) out.from = search["from"];
  if (typeof search["to"] === "string" && DAY.test(search["to"])) out.to = search["to"];
  return out;
}

export function StatementPage({ initialFrom, initialTo }: { initialFrom?: string | undefined; initialTo?: string | undefined }) {
  const navigate = useNavigate();
  const today = new Date();
  const [from, setFrom] = useState(initialFrom || isoDay(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [to, setTo] = useState(initialTo || isoDay(today));
  const [data, setData] = useState<StatementPeriod | null>(null);

  useEffect(() => {
    void navigate({ to: "/wallet/statement", search: { from, to }, replace: true });
  }, [from, to, navigate]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<"" | "pdf" | "email">("");

  const load = useCallback(() => {
    if (!from || !to) return;
    if (from > to) {
      setError("The start date must be before the end date.");
      return;
    }
    setError("");
    accountApi.statement(token(), from, to).then((result) => setData(result.statement)).catch((err: Error) => setError(err.message));
  }, [from, to]);

  useEffect(load, [load]);

  const pdf = async () => {
    setBusy("pdf");
    try {
      saveBlob(await accountApi.statementPdf(token(), from, to), `Jazia-statement-${from}-to-${to}.pdf`);
      JaziaToast.success("Statement downloaded", "Your statement was saved as a PDF.");
    } catch (err) {
      const { title, message } = errorOf(err, "Couldn't download the statement");
      JaziaToast.error(title, message);
    } finally {
      setBusy("");
    }
  };

  const email = async () => {
    setBusy("email");
    try {
      const result = await accountApi.emailStatement(token(), from, to);
      JaziaToast.success("Statement sent", result.message);
    } catch (err) {
      const { title, message } = errorOf(err, "Couldn't email the statement");
      JaziaToast.error(title, message);
    } finally {
      setBusy("");
    }
  };

  const csv = () => {
    if (!data) return;
    const head = ["Date", "Description", "Reference", "In (KES)", "Out (KES)", "Balance (KES)"];
    const lines = data.rows.map((row) => [fullDate(row.at), row.description, row.reference, row.inAmount ? row.inAmount.toFixed(2) : "", row.outAmount ? row.outAmount.toFixed(2) : "", row.balance == null ? "" : row.balance.toFixed(2)].map(csvCell).join(","));
    const intro = ["Jazia account statement", csvCell(data.businessName), `Account ${data.accountNumber}`, csvCell(data.periodLabel), `Opening KES ${data.opening.toFixed(2)},Money in KES ${data.moneyIn.toFixed(2)},Money out KES ${data.moneyOut.toFixed(2)},Closing KES ${data.closing.toFixed(2)}`, ""];
    saveBlob(new Blob([[...intro, head.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" }), `Jazia-statement-${from}-to-${to}.csv`);
  };

  return (
    <div className="jz-docm page">
      <div className="pg-head noprint">
        <div>
          <Link to="/wallet" className="back">{"\u2190"} Back to Account</Link>
          <h1>Account statement</h1>
          <p>Choose a period to view, download or email your statement.</p>
        </div>
        <button type="button" className="btn o" disabled={!data} onClick={() => window.print()}>Print</button>
      </div>
      <div className="box print wide" role="region" aria-label="Account statement">
      <div className="stm">
        <div>
          <div className="logo">Jazia<b>.</b></div>
          <div style={{ color: "var(--mute)" }}>Account statement</div>
        </div>
        <div className="right">
          <b>{data?.businessName || "\u00a0"}</b>
          <br />
          <span>Account no. {data?.accountNumber || ""}</span>
          <br />
          <span>{data?.periodLabel || ""}</span>
        </div>
      </div>
      <div className="period noprint">
        <div>
          <label htmlFor="st-from">From</label>
          <input id="st-from" type="date" value={from} max={to || undefined} onChange={(event) => setFrom(event.target.value)} />
        </div>
        <div>
          <label htmlFor="st-to">To</label>
          <input id="st-to" type="date" value={to} min={from || undefined} max={isoDay(today)} onChange={(event) => setTo(event.target.value)} />
        </div>
      </div>
      {error ? <div className="empty">{error}</div> : !data ? <div className="empty">Loading statement...</div> : (
        <>
          <div className="sum">
            <div><small>Opening balance</small><b className="mono">{kes(data.opening, 0)}</b></div>
            <div><small>Money in</small><b className="mono cr">{kes(data.moneyIn, 0)}</b></div>
            <div><small>Money out</small><b className="mono db">{kes(data.moneyOut, 0)}</b></div>
            <div><small>Closing balance</small><b className="mono">{kes(data.closing, 0)}</b></div>
          </div>
          <div className="tw">
            <table>
              <thead>
                <tr><th>Date</th><th>Description</th><th>Reference</th><th className="r">In</th><th className="r">Out</th><th className="r">Balance</th></tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr><td colSpan={6} className="empty">No completed transactions in this period.</td></tr>
                ) : data.rows.map((row) => (
                  <tr key={row.id}>
                    <td>{shortDate(row.at)}</td>
                    <td>{row.description}</td>
                    <td>{row.reference || "\u2014"}</td>
                    <td className={`r${row.inAmount ? " cr mono" : ""}`}>{row.inAmount ? plain(row.inAmount) : "\u2014"}</td>
                    <td className={`r${row.outAmount ? " db mono" : ""}`}>{row.outAmount ? plain(row.outAmount) : "\u2014"}</td>
                    <td className="r mono">{row.balance == null ? "\u2014" : `${row.balance < 0 ? "-" : ""}${plain(row.balance)}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <p className="btns end noprint">
        <button type="button" className="btn o" disabled={!data} onClick={csv}>Download spreadsheet</button>
        <button type="button" className="btn g" disabled={!data || busy !== ""} onClick={email}>{busy === "email" ? "Sending..." : "Email statement"}</button>
        <button type="button" className="btn" disabled={!data || busy !== ""} onClick={pdf}>{busy === "pdf" ? "Preparing..." : "Download PDF"}</button>
      </p>
      <div className="foot">{STATEMENT_FOOT}</div>
      </div>
    </div>
  );
}

// ---------- Withdraw ----------

type Pending = { id: string; number: string; amount: number; fee: number; total: number; destination: string };

export function WithdrawModal({
  data,
  onClose,
  onFinished,
}: {
  data: AccountStatement;
  onClose: () => void;
  onFinished: (result: { status: "COMPLETED" | "PROCESSING"; withdrawal: WithdrawalResult }) => void;
}) {
  const options = data.withdraw.destinations;
  const [channel, setChannel] = useState(options[0]?.channel || "");
  const [amountText, setAmountText] = useState("");
  const [step, setStep] = useState<"form" | "otp" | "processing">("form");
  const [pending, setPending] = useState<Pending | null>(null);
  const [sentTo, setSentTo] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [processingText, setProcessingText] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const destination = options.find((row) => row.channel === channel);
  const fee = destination?.fee || 0;
  const amount = Number(amountText);
  const typed = amountText.trim() !== "";
  const total = Math.round((amount + fee) * 100) / 100;

  let problem = "";
  if (typed && (!Number.isFinite(amount) || amount <= 0)) problem = "Enter an amount greater than zero.";
  else if (typed && total > data.balance + 0.001) {
    problem = `You don't have enough in your account to withdraw ${kes(amount)}${fee ? ` plus the ${kes(fee)} fee` : ""}. Your balance is ${kes(data.balance)}.`;
  } else if (typed && amount < data.withdraw.minAmount) problem = `The minimum withdrawal is ${kes(data.withdraw.minAmount)}.`;
  else if (typed && amount > data.withdraw.maxAmount) problem = `The most you can withdraw at once is ${kes(data.withdraw.maxAmount)}.`;

  const blocked = !data.withdraw.enabled
    ? "Withdrawals are paused right now. Please try again later."
    : !data.withdraw.verified
      ? "Your business must be verified before you can withdraw."
      : !options.length
        ? "Add an M-Pesa number or bank account in your business setup to withdraw."
        : "";

  const ready = !blocked && typed && !problem && Boolean(destination);

  const start = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    try {
      const result = await accountApi.startWithdrawal(token(), { channel, amount });
      setPending(result.withdrawal);
      setSentTo([result.challenge.destinations.phone, result.challenge.destinations.email].filter(Boolean).join(" and "));
      setOtp(["", "", "", "", "", ""]);
      setOtpError("");
      setCooldown(30);
      setStep("otp");
    } catch (error) {
      const { title, message } = errorOf(error, "Couldn't start the withdrawal");
      JaziaToast.error(title, message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (event: FormEvent) => {
    event.preventDefault();
    if (!pending || busy) return;
    if (otp.some((digit) => !digit)) {
      setOtpError("Enter all 6 digits.");
      return;
    }
    setBusy(true);
    setOtpError("");
    try {
      const result = await accountApi.confirmWithdrawal(token(), pending.id, otp.join(""));
      if (result.status === "COMPLETED") {
        JaziaToast.success("Withdrawal successful", result.message);
      } else {
        setProcessingText(result.message);
        setStep("processing");
      }
      onFinished(result);
    } catch (error) {
      const { title, message } = errorOf(error, "Code not accepted");
      setOtpError(message);
      setOtp(["", "", "", "", "", ""]);
      JaziaToast.error(title, message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!pending) return;
    try {
      const result = await accountApi.resendWithdrawalCode(token(), pending.id);
      setCooldown(30);
      JaziaToast.success("Code sent", result.message);
    } catch (error) {
      const { title, message } = errorOf(error, "Couldn't resend the code");
      JaziaToast.error(title, message);
    }
  };

  if (step === "processing") {
    return (
      <Shell label="Withdrawal processing" onClose={onClose}>
        <div className="rc">
          <div className="tick warn" aria-hidden="true">!</div>
          <div className="head-warn">Withdrawal processing</div>
          <div className="amt mono">{kes(pending?.amount || 0)}</div>
          <p className="sub">{processingText}</p>
          <p className="btns"><button type="button" className="btn" onClick={onClose}>Done</button></p>
        </div>
      </Shell>
    );
  }

  if (step === "otp" && pending) {
    return (
      <Shell label="Confirm withdrawal" onClose={() => !busy && onClose()}>
        <h2>Confirm your withdrawal</h2>
        <p className="sub">Enter the 6-digit code we sent to {sentTo || "your phone and email"}.</p>
        <div className="avail"><span>Sending</span><b className="mono">{kes(pending.amount)} to {pending.destination}</b></div>
        <form noValidate onSubmit={confirm}>
          <OtpInputs values={otp} onChange={setOtp} onClearError={() => setOtpError("")} invalid={Boolean(otpError)} />
          <div className={`msg center${otpError ? " bad" : ""}`} role="alert">{otpError || (busy ? "Checking code..." : `${kes(pending.total)} will be taken from your account.`)}</div>
          <p className="btns">
            <button type="button" className="btn o" disabled={busy} onClick={() => setStep("form")}>Back</button>
            <button type="submit" className="btn" disabled={busy}>{busy ? "Confirming..." : "Confirm withdrawal"}</button>
          </p>
          <p className="center msg">
            Didn't get it?{" "}
            <button type="button" className="link" disabled={cooldown > 0} onClick={resend}>{cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}</button>
          </p>
        </form>
      </Shell>
    );
  }

  return (
    <Shell label="Withdraw" onClose={() => !busy && onClose()}>
      <h2>Withdraw</h2>
      <p className="sub">Send money from your Jazia account to your M-Pesa or bank.</p>
      <div className="avail"><span>Available balance</span><b className="mono">{kes(data.balance)}</b></div>
      {blocked ? <div className="note">{blocked}</div> : null}
      <form noValidate onSubmit={start}>
        <div className="field">
          <label htmlFor="wd-to">Send to</label>
          <select id="wd-to" value={channel} disabled={!options.length} onChange={(event) => setChannel(event.target.value as typeof channel)}>
            {options.length ? options.map((row) => (
              <option key={row.channel} value={row.channel}>{`${row.channel === "MPESA" ? "M-Pesa" : "Bank"} \u2013 ${row.label}${row.name ? ` (${row.name})` : ""}`}</option>
            )) : <option value="">No payout account saved</option>}
          </select>
          {!options.length ? <div className="msg"><Link to="/onboarding" className="link">Add a payout account</Link></div> : null}
        </div>
        <div className="field">
          <label htmlFor="wd-amount">Amount</label>
          <div className="money">
            <span>KES</span>
            <input
              id="wd-amount"
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              className="mono"
              placeholder="0"
              value={amountText}
              aria-invalid={problem ? true : undefined}
              aria-describedby="wd-amount-msg"
              onChange={(event) => setAmountText(event.target.value)}
            />
          </div>
          <div id="wd-amount-msg" className={`msg${problem ? " bad" : ""}`} role={problem ? "alert" : undefined}>
            {problem || (typed && destination
              ? `${fee ? `Fee ${kes(fee)}. ` : "No fee. "}${kes(total)} will be taken from your account.`
              : `Minimum ${kes(data.withdraw.minAmount, 0)}, maximum ${kes(data.withdraw.maxAmount, 0)} per withdrawal.`)}
          </div>
        </div>
        <p className="btns end">
          <button type="button" className="btn o" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn" disabled={!ready || busy}>{busy ? "Sending code..." : "Withdraw"}</button>
        </p>
      </form>
    </Shell>
  );
}
